export const en = {
  title: 'Tickets',
  subtitle: 'Manage service requests and issues',
  create: 'New Ticket',
  createDesc: 'Create a new service request or issue report',
  submit: 'Submit Ticket',
  recent: 'Recent Tickets',
  empty: 'No Tickets',
  emptyDesc: 'No tickets yet',
  noFilterResult: 'No tickets matching the filter',

  // My tickets
  my: {
    title: 'My Tickets',
    subtitle: 'View and manage your tickets',
    created: 'Created by Me',
    assigned: 'Assigned to Me',
    emptyCreated: "You haven't created any tickets yet",
    emptyAssigned: 'No tickets assigned to you',
  },

  // List
  list: {
    title: 'Ticket List',
    total: '{total} tickets in total',
    desc: 'View and manage all tickets',
  },

  // Fields
  field: {
    title: 'Title',
    description: 'Description',
    category: 'Category',
    priority: 'Priority',
    status: 'Status',
    assignee: 'Assignee',
    creator: 'Creator',
    createdAt: 'Created At',
    tags: 'Tags',
    group: 'Group',
  },

  // Placeholders
  placeholder: {
    title: 'Briefly describe your issue',
    description: 'Describe your issue in detail, including:\n- What happened\n- When it occurred\n- What you have tried',
    category: 'Select a category',
    tags: 'Enter tag and press Enter',
    categoryName: 'Enter category name',
    categoryCode: 'Enter category code',
    groupName: 'Enter group name',
    groupCode: 'Enter group code',
  },

  // Filters
  filter: {
    allStatus: 'All Status',
    allPriority: 'All Priority',
    allCategory: 'All Category',
  },

  // Search
  search: {
    placeholder: 'Search tickets by title or number...',
  },

  // Status
  status: {
    open: 'Open',
    assigned: 'Assigned',
    inProgress: 'In Progress',
    pending: 'Pending',
    resolved: 'Resolved',
    closed: 'Closed',
    cancelled: 'Cancelled',
  },

  // Priority
  priority: {
    low: 'Low',
    medium: 'Medium',
    high: 'High',
    urgent: 'Urgent',
  },

  // Source
  source: {
    web: 'Web',
    email: 'Email',
    phone: 'Phone',
    chat: 'Chat',
    api: 'API',
  },

  // Stats
  stats: {
    total: 'Total Tickets',
    open: 'Open',
    inProgress: 'In Progress',
    resolved: 'Resolved',
    today: 'Today',
    todayCreated: 'Created Today',
    todayResolved: 'Resolved Today',
    slaRate: 'SLA Rate',
    satisfaction: 'Satisfaction',
  },

  // Comment
  comment: {
    placeholder: 'Enter your comment...',
  },

  // Validation
  validation: {
    titleRequired: 'Please enter a title',
    titleTooShort: 'Title must be at least 5 characters',
    descriptionRequired: 'Please enter a description',
    descriptionTooShort: 'Description must be at least 10 characters',
    categoryRequired: 'Please select a category',
    categoryNameRequired: 'Please enter category name',
    categoryCodeRequired: 'Please enter category code',
    categoryCodeFormat: 'Code must start with uppercase letter and contain only uppercase letters, numbers and underscores',
    codeExists: 'Code already exists',
    groupNameRequired: 'Please enter group name',
    groupCodeRequired: 'Please enter group code',
    groupCodeFormat: 'Code must start with uppercase letter and contain only uppercase letters, numbers and underscores',
  },

  // Assignment Strategy
  assignmentStrategy: {
    roundRobin: 'Round Robin',
    loadBalance: 'Load Balance',
    manual: 'Manual',
    skillBased: 'Skill Based',
  },

  // Tips
  tips: {
    title: 'Tips',
    clear: 'Please describe your issue in detail',
    category: 'Selecting the right category helps faster resolution',
    priority: 'Select "Urgent" for critical issues',
  },

  // Success messages
  success: {
    created: 'Ticket created successfully',
    updated: 'Updated successfully',
    deleted: 'Deleted successfully',
    statusUpdated: 'Status updated',
    commentAdded: 'Comment added',
    watched: 'Now watching',
    unwatched: 'Stopped watching',
    rated: 'Thank you for your feedback',
  },

  // Error messages
  error: {
    loadFailed: 'Failed to load',
    createFailed: 'Failed to create',
    updateFailed: 'Failed to update',
    deleteFailed: 'Failed to delete',
    commentFailed: 'Failed to add comment',
    rateFailed: 'Failed to submit rating',
    notFound: 'Ticket not found',
    loadCategoriesFailed: 'Failed to load categories',
    saveFailed: 'Failed to save',
  },

  // Confirm
  confirm: {
    deleteCategory: 'Are you sure you want to delete category "{name}"?',
  },

  // Admin
  admin: {
    categories: 'Categories',
    groups: 'Assignment Groups',
    stats: 'Statistics',
  },

  // Navigation
  nav: {
    overview: 'Overview',
    list: 'Ticket List',
    my: 'My Tickets',
    new: 'New Ticket',
  },

  // Overview page
  overview: {
    title: 'Ticket Overview',
    loading: 'Loading...',
    newTicket: 'New Ticket',
    newTicketDesc: 'Create a new service request or issue report',
    allTickets: 'All Tickets',
    open: 'Open',
    inProgress: 'In Progress',
    resolved: 'Resolved',
    todayStats: 'Today Stats',
    todayCreated: 'Created Today',
    todayResolved: 'Resolved Today',
    slaRate: 'SLA Rate',
    satisfaction: 'Satisfaction',
    quickActions: 'Quick Actions',
    myTickets: 'My Tickets',
    myTicketsDesc: 'View and manage your tickets',
    ticketList: 'Ticket List',
    ticketListDesc: 'View and manage all tickets',
    recentTickets: 'Recent Tickets',
    viewAll: 'View All',
    noTickets: 'No Tickets',
    noTicketsDesc: 'No tickets yet',
    assignee: 'Assignee',
  },

  // New ticket page
  newTicket: {
    title: 'New Ticket',
    back: 'Back',
    cancel: 'Cancel',
    submit: 'Submit Ticket',
    submitting: 'Submitting...',
    titleLabel: 'Title',
    titlePlaceholder: 'Briefly describe your issue',
    categoryLabel: 'Category',
    categoryPlaceholder: 'Select a category',
    priorityLabel: 'Priority',
    descriptionLabel: 'Description',
    descriptionPlaceholder: 'Describe your issue in detail, including:\n- What happened\n- When it occurred\n- What you have tried',
    tagsLabel: 'Tags',
    tagsOptional: '(Optional, max 5)',
    tagsPlaceholder: 'Enter tag and press Enter',
    addTag: 'Add',
    tipsTitle: 'Tips',
    tip1: 'Please describe your issue in as much detail as possible',
    tip2: 'Selecting the correct category helps with faster processing',
    tip3: 'For urgent issues, please select "Urgent" priority',
    loadCategoriesFailed: 'Failed to load categories',
    createFailed: 'Failed to create ticket',
  },

};

export default en;
